import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  SHARE_DESTINATIONS,
  fetchVideoBlob,
  filenameForReel,
  shareReelToDestination,
  shareTextForReel,
} from "../../utils/clipPersonalShare";
import "./ClipShareSheet.css";

function InstagramMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" />
    </svg>
  );
}

function TikTokMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M14.2 3.2c.7 4.1 3.2 5.6 6.3 5.8v3.3c-2.1 0-4-.6-6.2-1.8v6.7c0 4.4-3.3 6.8-6.7 6.8-2.1 0-4.1-.8-5.4-2.2 1.3 1 2.9 1.4 4.5 1.1 2.6-.5 4.4-2.7 4.4-5.7V8.4c1.9 1.4 4.1 2.2 6.5 2.3V7.4c-1.9-.3-3.6-1.4-4.6-3.1H9.8V3.2h4.4Z"
        fill="currentColor"
      />
    </svg>
  );
}

function WhatsAppMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3.2A8.7 8.7 0 0 0 4.4 16.4L3 21l4.7-1.4A8.7 8.7 0 1 0 12 3.2Zm4.9 12.3c-.2.6-1.2 1.1-1.7 1.1-.4 0-.9.2-3-.8-2.5-1.2-4.1-3.6-4.2-3.8-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5.2.6.7 2 .8 2.1.1.2.1.3 0 .5-.1.2-.2.3-.3.5-.2.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.5.1.7-.1.2-.2.8-.9 1-1.2.2-.3.4-.2.7-.1.3.1 1.9.9 2.2 1.1.3.2.5.3.6.4.1.2.1.9-.2 1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function FacebookMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M14.2 21v-7.2h2.4l.4-2.8h-2.8V9.3c0-.8.2-1.4 1.4-1.4H17V5.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 4v2.7H8.3v2.8h2.6V21h3.3Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MoreMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="5" cy="12" r="1.8" fill="currentColor" />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" />
      <circle cx="19" cy="12" r="1.8" fill="currentColor" />
    </svg>
  );
}

function DownloadMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 4v10.2M8.2 10.8 12 14.6l3.8-3.8M5 18.5h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ICONS = {
  instagram: InstagramMark,
  tiktok: TikTokMark,
  whatsapp: WhatsAppMark,
  facebook: FacebookMark,
  more: MoreMark,
  download: DownloadMark,
};

export default function ClipShareSheet({
  open,
  onClose,
  videoUrl = null,
  blob = null,
  kind = "max_rally",
  t = (key, fallback) => fallback || key,
}) {
  const [busyId, setBusyId] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [resolvedBlob, setResolvedBlob] = useState(blob || null);

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    setStatus("");
    setBusyId(null);
    setResolvedBlob(blob || null);
    return undefined;
  }, [open, blob, videoUrl]);

  const title = useMemo(
    () =>
      kind === "stroke_slowmo"
        ? t("shareReelTitleSlowMo", "Share your slow-mo reel")
        : t("shareReelTitle", "Share your Max Rally"),
    [kind, t]
  );
  const filename = useMemo(() => filenameForReel(kind), [kind]);
  const shareText = useMemo(() => shareTextForReel(kind, t), [kind, t]);
  const previewSrc = useMemo(() => {
    if (videoUrl) return videoUrl;
    if (resolvedBlob) return URL.createObjectURL(resolvedBlob);
    return "";
  }, [videoUrl, resolvedBlob]);

  useEffect(() => {
    if (videoUrl || !previewSrc.startsWith("blob:")) return undefined;
    return () => URL.revokeObjectURL(previewSrc);
  }, [previewSrc, videoUrl]);

  const ensureBlob = useCallback(async () => {
    if (resolvedBlob) return resolvedBlob;
    if (blob) {
      setResolvedBlob(blob);
      return blob;
    }
    if (!videoUrl) throw new Error("Missing video");
    const fetched = await fetchVideoBlob(videoUrl);
    setResolvedBlob(fetched);
    return fetched;
  }, [blob, resolvedBlob, videoUrl]);

  const handleShare = useCallback(
    async (destination) => {
      setError("");
      setStatus("");
      setBusyId(destination);
      try {
        const clip = await ensureBlob();
        const result = await shareReelToDestination({
          destination,
          blob: clip,
          filename,
          title,
          text: shareText,
        });
        if (result.action === "cancelled") {
          setStatus("");
          return;
        }
        if (result.action === "downloaded_then_open") {
          const dest = SHARE_DESTINATIONS.find((item) => item.id === destination);
          setStatus(
            t("shareReelOpenApp", "Video saved. Open {{app}} and pick it from Recents to post.", {
              app: dest?.defaultLabel || destination,
            })
          );
          return;
        }
        if (result.action === "downloaded") {
          setStatus(t("shareReelDownloaded", "Video saved"));
          return;
        }
        setStatus(t("shareReelSent", "Ready — pick the app to post"));
      } catch (err) {
        setError(
          err?.message ||
            t("shareReelFailed", "Couldn’t prepare this reel for sharing.")
        );
      } finally {
        setBusyId(null);
      }
    },
    [ensureBlob, filename, shareText, t, title]
  );

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="clip-share-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clip-share-sheet-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="clip-share-sheet__panel">
        <div className="clip-share-sheet__handle" aria-hidden />
        <header className="clip-share-sheet__header">
          <div>
            <h2 id="clip-share-sheet-title">{title}</h2>
            <p>
              {t(
                "shareReelSubtitle",
                "Post to Instagram, TikTok, WhatsApp, or save the video."
              )}
            </p>
          </div>
          <button
            type="button"
            className="clip-share-sheet__close"
            onClick={onClose}
            aria-label={t("close", "Close")}
          >
            ×
          </button>
        </header>

        <div className="clip-share-sheet__preview">
          {previewSrc ? (
            <video
              src={previewSrc}
              playsInline
              muted
              loop
              autoPlay
              controls
            />
          ) : (
            <div className="clip-share-sheet__preview-empty">
              {t("shareReelPreparing", "Preparing video…")}
            </div>
          )}
        </div>

        <div className="clip-share-sheet__grid" role="list">
          {SHARE_DESTINATIONS.map((dest) => {
            const Icon = ICONS[dest.id];
            const busy = busyId === dest.id;
            return (
              <button
                key={dest.id}
                type="button"
                className={`clip-share-sheet__dest clip-share-sheet__dest--${dest.id}`}
                onClick={() => handleShare(dest.id)}
                disabled={Boolean(busyId)}
                role="listitem"
              >
                <span className="clip-share-sheet__icon">{Icon ? <Icon /> : null}</span>
                <span>
                  {busy
                    ? t("shareReelPreparing", "Preparing video…")
                    : t(dest.labelKey, dest.defaultLabel)}
                </span>
              </button>
            );
          })}
        </div>

        <p className="clip-share-sheet__hint">
          {t(
            "shareReelHintPersonal",
            "Instagram and TikTok don’t let apps post to personal profiles. We’ll save the file so you can post it yourself."
          )}
        </p>
        {status ? <p className="clip-share-sheet__status">{status}</p> : null}
        {error ? <p className="clip-share-sheet__error">{error}</p> : null}
      </div>
    </div>,
    document.body
  );
}
